import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from '@/components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import { API_URL } from '../../config';
import useGuestTracking from '../../hooks/useGuestTracking';

const Landing = ({ onEnter }) => {
  useGuestTracking();
  // Guest Traffic Tracking
  const [menuOpen, setMenuOpen] = useState(false);
  const [typewriterText, setTypewriterText] = useState('');
  const [showCursor, setShowCursor] = useState(true);
  const fullText = 'Deine digitale Identität startet hier.';
  
  // Auto-play plugin for Julia Carousel (4 seconds)
  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: true })
  );
  
  useEffect(() => {
    let currentIndex = 0;
    
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullText.length) {
        setTypewriterText(fullText.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
        // Keep cursor blinking after text is complete
        setShowCursor(true);
      }
    }, 70); // 70ms pro Buchstabe für flüssigeres Typing
    
    return () => clearInterval(typingInterval);
  }, [fullText]);

  const handleCookieSettings = () => {
    window.dispatchEvent(new Event('openCookieSettings'));
  };

  return (
    <div className="min-h-screen bg-black grain-texture relative">

      {/* Hamburger Menu */}
      <button
        onClick={() => setMenuOpen(!menuOpen)}
        className="fixed top-6 right-6 z-50 p-3 hover:bg-white/10 rounded-lg transition-all"
        aria-label="Menu"
      >
        <div className="w-6 h-5 flex flex-col justify-between">
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? 'opacity-0' : ''}`}></span>
          <span className={`w-full h-0.5 bg-white transition-all ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
        </div>
      </button>

      {/* Side Menu */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col p-8 space-y-6 mt-20">
          
          <Link to="/was-ist-snapshot" className="text-white hover:text-[#10B981] transition-colors text-lg font-medium">
            Was ist Snapshot?
          </Link>
          
          <Link to="/" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            Beispiel-Avatar
          </Link>
          
          <Link to="/impressum" className="text-white/60 hover:text-white transition-colors text-sm">
            Impressum
          </Link>
          <Link to="/datenschutz" className="text-white/60 hover:text-white transition-colors text-sm">
            Datenschutz
          </Link>
          <Link to="/agb" className="text-white/60 hover:text-white transition-colors text-sm">
            AGB
          </Link>
          <Link to="/kontakt" className="text-white/60 hover:text-white transition-colors text-sm">
            Kontakt
          </Link>
          <button onClick={handleCookieSettings} className="text-white/60 hover:text-white transition-colors text-sm text-left">
            Cookie-Einstellungen
          </button>
        </nav>
      </div>

      {/* Main Content */}
      <div className="min-h-screen flex flex-col items-center justify-center px-6 relative pt-8 sm:pt-0">
        {/* Logo & Branding */}
        <div className="text-center mb-12 animate-fade-in">
          {/* Camera Icon */}
          <div className="mb-8 flex justify-center">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#10B981] to-[#059669] flex flex-col items-center justify-center glow-green relative">
              {/* Camera Icon - mittig */}
              <svg className="w-9 h-9 text-black mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {/* ORIGINAL Text - mittig unter Icon */}
              <span className="text-black text-[9px] font-black tracking-widest">ORIGINAL</span>
            </div>
          </div>
          
          {/* Logo Text */}
          <h1 className="text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter mb-4">
            <span className="text-white">SNAPSHOT</span>
          </h1>
          
          {/* Tagline with Typewriter Effect */}
          <p className="text-xl md:text-2xl text-white/80 font-light max-w-2xl mx-auto leading-relaxed min-h-[2em] flex items-center justify-center">
            <span className="typewriter-text">{typewriterText}</span>
            {showCursor && <span className="inline-block w-0.5 h-6 bg-[#10B981] ml-1 animate-pulse"></span>}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col w-full max-w-md mb-16">
          {/* Primary Buttons Row */}
          <div className="flex flex-col sm:flex-row gap-4 mb-3">
            {/* Anmelden Button */}
            <button
              onClick={() => onEnter('login')}
              className="flex-1 px-6 py-3 sm:px-8 sm:py-4 bg-black text-white border-2 border-white rounded-xl font-semibold text-base sm:text-lg hover:bg-white hover:text-black transition-all duration-300"
              data-testid="login-button"
            >
              Anmelden
            </button>
            
            {/* Registrieren Button (Green Glow) */}
            <button
              onClick={() => onEnter('register')}
              className="flex-1 px-6 py-3 sm:px-8 sm:py-4 bg-[#10B981] text-black rounded-xl font-semibold text-base sm:text-lg hover:bg-[#059669] transition-all duration-300 glow-green pulse-glow shine-effect"
              data-testid="register-button"
            >
              Registrieren
            </button>
          </div>

          {/* Divider - HIDDEN */}
          <div className="hidden flex items-center gap-4 my-4 sm:my-6">
            <div className="flex-1 h-px bg-white/20"></div>
            <span className="text-white/40 text-sm font-medium">oder mit</span>
            <div className="flex-1 h-px bg-white/20"></div>
          </div>

          {/* Google Login Button - HIDDEN */}
          <button
            onClick={() => {
              const redirectUrl = encodeURIComponent(`${window.location.origin}/dashboard`);
              window.location.href = `https://auth.emergentagent.com/?redirect=${redirectUrl}`;
            }}
            className="hidden w-full px-4 py-2 bg-white/5 border-2 border-white/20 rounded-xl hover:bg-white/10 hover:border-white/30 transition-all duration-300 items-center justify-center gap-3 font-semibold text-base"
            data-testid="google-login-button"
            aria-label="Mit Google anmelden"
            title="Mit Google anmelden"
          >
            <svg className="w-6 h-6 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <span className="text-white">Mit Google anmelden</span>
          </button>

          {/* Was ist Snapshot? Button - KLEINER */}
          <Link
            to="/was-ist-snapshot"
            className="mt-4 px-3 py-1.5 bg-[#10B981]/10 text-[#10B981] rounded-lg border border-[#10B981]/20 hover:bg-[#10B981]/20 hover:border-[#10B981]/40 transition-all font-medium text-xs flex items-center justify-center gap-1.5 max-w-[180px] mx-auto opacity-70 hover:opacity-100"
            data-testid="was-ist-snapshot-button"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Was ist SNAPSHOT?
          </Link>

          {/* Animated Scroll Down Arrow - MEGA SICHTBAR */}
          <button
            onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
            className="mt-10 flex flex-col items-center gap-2 animate-bounce-slow hover:opacity-70 transition-opacity"
            aria-label="Scroll down for more info"
          >
            <span className="text-[#10B981] text-base sm:text-lg font-bold glow-green px-4 py-2 bg-black/50 rounded-full border border-[#10B981]/30">👇 ich zeig's dir 👇</span>
            <svg className="w-8 h-8 text-[#10B981] glow-green" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        </div>

        {/* Subtle Ambient Glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#10B981] opacity-5 blur-3xl rounded-full pointer-events-none"></div>
      </div>

      {/* Sektion 1: Julia Intro - Real trifft Digital - NÄHER AN BOUNCE */}
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center px-4 py-4 sm:py-8 -mt-8 sm:-mt-12">
        <div className="max-w-6xl w-full">
          
          {/* Header */}
          <div className="text-center mb-10 opacity-0 animate-fade-in-up">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-8">
              Real trifft Digital – <span className="text-[#10B981] glow-green">Willkommen bei SNAPSHOT</span>
            </h2>
            <p className="text-white/60 text-lg mb-2">
              Julia Beitler bekannt aus:
            </p>
            <p className="text-white/80 text-xl font-semibold mb-6">
              Kabel Eins – Trucker Babes & Abenteuer Leben
            </p>
            
            {/* Grüner Teaser-Text */}
            <p className="text-[#10B981] text-base md:text-lg leading-relaxed max-w-3xl mx-auto font-semibold glow-green">
              Julia testet X-clusive SnapshotPro.ai – ihre Avatare sehen so echt aus, dass ihre Fans den Unterschied kaum erkennen. Modernste KI für maximale Realität.
            </p>
          </div>

          {/* Images Comparison */}
          <div className="grid md:grid-cols-2 gap-8 mb-6 mt-12 md:mt-16">
            
            {/* Julia Original */}
            <div className="group relative opacity-0 animate-fade-in-left">
              <div className="relative overflow-hidden rounded-2xl border-2 border-white/10 hover:border-[#10B981]/30 transition-all duration-500">
                <img 
                  src="https://customer-assets.emergentagent.com/job_306e1c89-de43-4d48-9a73-6232bec6d105/artifacts/xcvu6odi_Bildschirmfoto%202025-11-07%20um%2021.27.46.png" 
                  alt="Julia Original" 
                  className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">📸 Julia</h3>
                  <p className="text-white/70">Das Original</p>
                </div>
              </div>
            </div>

            {/* Julia Avatar Carousel - AUTO-PLAY */}
            <div className="group relative opacity-0 animate-fade-in-right">
              <Carousel
                plugins={[autoplayPlugin.current]}
                opts={{
                  align: "start",
                  loop: true,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {/* Slide 1: LKW */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/zbcgpzmk_SNAPSHOT.png" 
                        alt="Julia Avatar - LKW" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 2: Auto Sonnenuntergang */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/94xmivhv_SNAPSHOT2.jpg" 
                        alt="Julia Avatar - Auto Sonnenuntergang" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 3: Leder-Outfit Reifen */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/zfghuegt_SNAPSHOT3.JPG" 
                        alt="Julia Avatar - Leder-Outfit" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 4: Werkstatt */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/clwgebyt_SNAPSHOT4.JPG" 
                        alt="Julia Avatar - Werkstatt" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 5: Winterstraße */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/alxh5x2p_SNAPSHOT5.jpg" 
                        alt="Julia Avatar - Winterstraße" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 6: Lederjacke Blau */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/h12wd0o6_SNAPSHOT6.jpg" 
                        alt="Julia Avatar - Lederjacke" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 7: Grüner Overall Werkstatt */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/wk9ibv41_SNAPSHOT7.JPG" 
                        alt="Julia Avatar - Werkstatt Overall" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 8: Trucker Power */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/91g11c8m_SNAPSHOT9.jpg" 
                        alt="Julia Avatar - Trucker Power" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 9: Oldtimer Glamour */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/my1cz2z1_SNAPSHOT10.JPG" 
                        alt="Julia Avatar - Oldtimer Glamour" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>

                  {/* Slide 10: Badass Style */}
                  <CarouselItem>
                    <div className="relative overflow-hidden rounded-2xl border-2 border-[#10B981]/30 hover:border-[#10B981] transition-all duration-500 bg-black">
                      <img 
                        src="https://customer-assets.emergentagent.com/job_real-auth-system/artifacts/kporoxvo_SNAPSHOT11.JPG" 
                        alt="Julia Avatar - Badass Style" 
                        className="w-full h-[500px] md:h-[650px] object-cover md:object-[center_30%]"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
                        <h3 className="text-2xl font-bold text-white mb-2">🤖 Julias Avatar</h3>
                        <p className="text-[#10B981]">SNAPSHOT Perfection</p>
                      </div>
                    </div>
                  </CarouselItem>
                </CarouselContent>
                
                {/* Navigation Arrows - DEUTLICH SICHTBARER */}
                <CarouselPrevious className="left-2 md:left-4 bg-[#10B981]/80 border-[#10B981] hover:bg-[#10B981] text-black shadow-lg glow-green" />
                <CarouselNext className="right-2 md:right-4 bg-[#10B981]/80 border-[#10B981] hover:bg-[#10B981] text-black shadow-lg glow-green" />
              </Carousel>
            </div>

          </div>

          {/* Julia Quote - UNTER DEN BILDERN - Kursiv */}
          <div className="text-center mb-12 opacity-0 animate-fade-in-up">
            <p className="text-lg md:text-xl text-[#10B981] font-light glow-green leading-relaxed max-w-4xl mx-auto mb-2 italic">
              "Ich hätte nie gedacht, dass eine App sowas hinkriegt." – Ich liebe mein Digitales ich
            </p>
            <p className="text-white/60 text-base md:text-lg max-w-3xl mx-auto leading-relaxed mt-6">
              SNAPSHOT macht die Magie: Deine Fotos werden zu ultra-realistischen KI-Bildern – 95 % Gesichtserkennung, modernste KI, easy zu bedienen.
            </p>
          </div>

          {/* Divider */}
          <div className="flex items-center justify-center my-8 md:my-16 opacity-0 animate-fade-in-up">
            <div className="flex-1 h-px md:h-0.5 bg-gradient-to-r from-transparent via-[#10B981]/50 to-transparent max-w-xs md:max-w-2xl"></div>
          </div>

        </div>
      </div>

      {/* Sektion 2: So einfach funktioniert SNAPSHOT */}
      <div className="min-h-screen bg-black grain-texture flex items-center justify-center px-4 py-8 md:py-12" id="wie-funktioniert">
        <div className="max-w-5xl w-full">
          
          {/* Header */}
          <div className="text-center mb-14 -mt-20 md:-mt-32">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 opacity-0 animate-fade-in-up">
              So einfach funktioniert <span className="text-[#10B981] glow-green">SNAPSHOT</span>
            </h2>
          </div>

          {/* 4-Schritte Prozess - DESIGN-OPTIMIERT */}
          <div className="grid sm:grid-cols-2 gap-8 md:gap-10 mb-16 mt-16">
            
            {/* Schritt 1: Kostenlos registrieren */}
            <div className="group p-8 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl hover:border-[#10B981]/50 hover:bg-white/[0.06] transition-all duration-500 opacity-0 animate-fade-in-up hover:shadow-[0_0_30px_rgba(16,185,129,0.15)] hover:scale-[1.02]">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#10B981]/20 group-hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all duration-500 group-hover:scale-110">
                  <svg className="w-9 h-9 text-[#10B981] transition-all duration-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#10B981] transition-colors duration-500">1. Kostenlos registrieren</h3>
                  <p className="text-sm md:text-base text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-500">
                    Erstelle dein Konto in 30 Sekunden und erhalte deine eigene <span className="text-[#10B981] font-semibold">ID-Card</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Schritt 2: Paket wählen */}
            <div className="group p-8 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl hover:border-[#10B981]/50 hover:bg-white/[0.06] transition-all duration-500 opacity-0 animate-fade-in-up hover:shadow-[0_0_30px_rgba(16,185,129,0.15)] hover:scale-[1.02]" style={{animationDelay: '0.1s'}}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#10B981]/20 group-hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all duration-500 group-hover:scale-110">
                  <svg className="w-9 h-9 text-[#10B981] transition-all duration-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#10B981] transition-colors duration-500">2. Snaps kaufen</h3>
                  <p className="text-sm md:text-base text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-500">
                    Wähle dein gewünschtes Guthaben<br/>
                    Flexible Beträge ab 10 €
                  </p>
                </div>
              </div>
            </div>

            {/* Schritt 3: Digitales Ich erstellen */}
            <div className="group p-8 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl hover:border-[#10B981]/50 hover:bg-white/[0.06] transition-all duration-500 opacity-0 animate-fade-in-up hover:shadow-[0_0_30px_rgba(16,185,129,0.15)] hover:scale-[1.02]" style={{animationDelay: '0.2s'}}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#10B981]/20 group-hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all duration-500 group-hover:scale-110 group-hover:rotate-12">
                  <span className="text-4xl transition-all duration-500">🤖</span>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#10B981] transition-colors duration-500">3. Dein digitales Ich erstellen</h3>
                  <p className="text-sm md:text-base text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-500">
                    Lade <span className="text-white font-semibold">10 Selfies</span> in verschiedenen Positionen hoch<br/>
                    <span className="text-[#10B981] font-semibold">Unsere KI studiert 6-8 Min deine Gesichtszüge</span><br/>
                    <span className="text-white/50 italic text-xs md:text-sm">(Du wartest einfach nur - unsere KI arbeitet für dich!)</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Schritt 4: Traumbilder generieren */}
            <div className="group p-8 bg-white/[0.03] backdrop-blur-sm border border-white/10 rounded-3xl hover:border-[#10B981]/50 hover:bg-white/[0.06] transition-all duration-500 opacity-0 animate-fade-in-up hover:shadow-[0_0_30px_rgba(16,185,129,0.15)] hover:scale-[1.02]" style={{animationDelay: '0.3s'}}>
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-2xl bg-[#10B981]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#10B981]/20 group-hover:shadow-[0_0_25px_rgba(16,185,129,0.4)] transition-all duration-500 group-hover:scale-110">
                  <svg className="w-9 h-9 text-[#10B981] transition-all duration-500 drop-shadow-[0_0_8px_rgba(16,185,129,0.6)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-[#10B981] transition-colors duration-500">4. Traumbilder generieren</h3>
                  <p className="text-sm md:text-base text-white/70 leading-relaxed group-hover:text-white/90 transition-colors duration-500">
                    Unbegrenzte Möglichkeiten!<br/>
                    <span className="text-[#10B981] font-semibold">⚡ Jedes Bild in 30-60 Sekunden fertig</span>
                  </p>
                </div>
              </div>
            </div>

          </div>

          {/* Zusammenfassung - NACH den 4 Schritten */}
          <div className="text-center mb-10 mt-8">
            <p className="text-white/80 text-lg md:text-xl leading-relaxed max-w-3xl mx-auto opacity-0 animate-fade-in-up mb-6">
              Mit SNAPSHOT erstellst du in wenigen Minuten deinen ganz persönlichen KI-Avatar. 
              Lade einfach deine Fotos hoch, wähle deinen Stil, und sieh zu, wie dein Avatar zum Leben erwacht.
            </p>
            
            <p className="text-[#10B981] text-lg md:text-xl leading-relaxed max-w-3xl mx-auto opacity-0 animate-fade-in-up font-semibold glow-green">
              Wir bringen dich überall hin – von den Malediven bis nach Manhattan.<br/>
              Ob Business, Travel oder Lifestyle:<br/>
              Dein digitales Ich erlebt, was du dir wünschst – in Sekundenschnelle.
            </p>
          </div>

          {/* CTA Button - Erstelle jetzt dein digitales Ich */}
          <div className="text-center opacity-0 animate-fade-in-up">
            <button
              onClick={() => {
                // Scroll to top where Login/Register buttons are
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-block px-10 py-5 bg-[#10B981] text-black rounded-xl font-bold text-xl hover:bg-[#059669] transition-all duration-300 glow-green pulse-glow shine-effect shadow-lg cursor-pointer"
              data-testid="create-digital-self-cta"
            >
              Erstelle jetzt dein digitales Ich
            </button>
          </div>

        </div>
      </div>

      {/* Instagram Follow - Zwischen CTA und Footer */}
      <div className="bg-black grain-texture py-12">
        <div className="text-center max-w-md mx-auto px-4">
          <p className="text-white/50 text-sm mb-4">Folge uns für Inspiration & Updates</p>
          <a
            href="https://www.instagram.com/snapshotpro.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full font-bold text-lg hover:from-purple-600 hover:to-pink-600 transition-all shadow-lg"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            @snapshotpro.ai
          </a>
        </div>
      </div>

      {/* Footer - Always at Bottom */}
      <footer className="bg-black grain-texture py-8 border-t border-white/10">
        <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-sm text-white/50 max-w-4xl mx-auto items-center px-4">
          <Link to="/datenschutz" className="hover:text-white transition-colors">
            Datenschutz
          </Link>
          <span className="text-white/20">•</span>
          <Link to="/impressum" className="hover:text-white transition-colors">
            Impressum
          </Link>
          <span className="text-white/20">•</span>
          <Link to="/agb" className="hover:text-white transition-colors">
            AGB
          </Link>
          <span className="text-white/20">•</span>
          <Link to="/kontakt" className="hover:text-white transition-colors">
            Kontakt
          </Link>
          <span className="text-white/20">•</span>
          <button onClick={handleCookieSettings} className="hover:text-white transition-colors">
            Cookie-Einstellungen
          </button>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
